/**
 * AI Code Governance - Policies
 *
 * Route: /code-governance/policies | Nav: Code > AI Code Governance > Policies
 *
 * Deterministic enforcement rules applied to every pull request.
 * Shows all 10 policies from the AI governance policy pack with
 * severity, mode, enable toggle, description, and control/framework mappings.
 */
import React, { useState } from 'react';
import {
  Title2,
  Body1,
  Body2,
  Caption1,
  Card,
  Badge,
  Switch,
  tokens,
  makeStyles,
} from '@fluentui/react-components';
import { usePageStyles } from '../styles/page-styles';
import { PageHeader } from '../components/common/PageHeader';

// ---------------------------------------------------------------------------
// Custom styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  policyCard: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalL,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  policyHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
  },
  policyHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    flex: 1,
    flexWrap: 'wrap' as const,
  },
  policyKey: {
    fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
    fontWeight: 600,
    fontSize: tokens.fontSizeBase300,
  },
  badgeRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap' as const,
  },
  mappingsRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap' as const,
    alignItems: 'center',
  },
  categoryLabel: {
    fontWeight: 600,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    minWidth: '80px',
  },
});

// ---------------------------------------------------------------------------
// Demo data
// ---------------------------------------------------------------------------

type Severity = 'CRITICAL' | 'HIGH' | 'MEDIUM';
type Mode = 'BLOCK' | 'WARN';

interface GovernancePolicy {
  key: string;
  name: string;
  category: string;
  severity: Severity;
  mode: Mode;
  enabled: boolean;
  description: string;
  controls: string[];
  frameworks: string[];
}

const initialPolicies: GovernancePolicy[] = [
  {
    key: 'AI-APP-001',
    name: 'Unvalidated External Input',
    category: 'Application Security',
    severity: 'HIGH',
    mode: 'BLOCK',
    enabled: true,
    description: 'Detects request parameters, query strings, or body fields used without validation or sanitization. Unvalidated input is the root cause of injection attacks, data corruption, and application crashes.',
    controls: ['SI-10', 'SC-18'],
    frameworks: ['SOC 2', 'ISO 27001', 'NIST 800-53'],
  },
  {
    key: 'AI-DATA-001',
    name: 'Sensitive Data Logging',
    category: 'Data Protection',
    severity: 'CRITICAL',
    mode: 'BLOCK',
    enabled: true,
    description: 'Flags PII, credentials, or financial data written to log outputs. Sensitive data in logs can be exposed through log aggregation services, violating privacy regulations and enabling identity theft.',
    controls: ['AU-3', 'SC-28', 'SI-12'],
    frameworks: ['SOC 2', 'PCI DSS', 'HIPAA', 'GDPR'],
  },
  {
    key: 'AI-SEC-001',
    name: 'Hardcoded Secret / Token Pattern',
    category: 'Secrets Management',
    severity: 'CRITICAL',
    mode: 'BLOCK',
    enabled: true,
    description: 'Identifies API keys, connection strings, passwords, or tokens embedded in source code. Hardcoded secrets in version control are permanently exposed once pushed, even if later removed.',
    controls: ['SC-12', 'SC-28', 'IA-5'],
    frameworks: ['SOC 2', 'PCI DSS', 'ISO 27001'],
  },
  {
    key: 'AI-NET-001',
    name: 'Insecure External HTTP Call',
    category: 'Network Security',
    severity: 'HIGH',
    mode: 'BLOCK',
    enabled: true,
    description: 'Detects HTTP (non-TLS) calls to external endpoints or calls with disabled certificate verification. Data transmitted without encryption is visible to any network observer between source and destination.',
    controls: ['SC-8', 'SC-23'],
    frameworks: ['SOC 2', 'ISO 27001', 'NIST 800-53'],
  },
  {
    key: 'AI-AUTH-001',
    name: 'Authorization Bypass Risk',
    category: 'Access Control',
    severity: 'CRITICAL',
    mode: 'BLOCK',
    enabled: true,
    description: 'Flags endpoints or handlers missing authorization checks, role requirements, or policy enforcement. Without authorization, any authenticated user can access restricted functionality, escalating privileges.',
    controls: ['AC-3', 'AC-6', 'AC-17'],
    frameworks: ['SOC 2', 'ISO 27001', 'NIST 800-53', 'PCI DSS'],
  },
  {
    key: 'AI-EXEC-001',
    name: 'Unsafe Dynamic Execution',
    category: 'Code Integrity',
    severity: 'CRITICAL',
    mode: 'BLOCK',
    enabled: true,
    description: 'Detects eval(), Function(), or dynamic code execution with user-controllable input. Dynamic execution enables remote code execution (RCE), the most severe class of vulnerability.',
    controls: ['SI-3', 'SI-10', 'CM-7'],
    frameworks: ['SOC 2', 'ISO 27001', 'OWASP Top 10'],
  },
  {
    key: 'AI-DB-001',
    name: 'Database Query Without Parameterization',
    category: 'Data Access',
    severity: 'HIGH',
    mode: 'BLOCK',
    enabled: true,
    description: 'Flags SQL or NoSQL queries built with string concatenation or interpolation. Non-parameterized queries enable SQL injection, allowing attackers to read, modify, or delete arbitrary database records.',
    controls: ['SI-10', 'SC-28'],
    frameworks: ['SOC 2', 'PCI DSS', 'OWASP Top 10'],
  },
  {
    key: 'AI-GOV-001',
    name: 'AI Policy Annotation Missing',
    category: 'Governance',
    severity: 'MEDIUM',
    mode: 'WARN',
    enabled: true,
    description: 'Warns when AI-generated or AI-modified code lacks a governance annotation. Without annotations, audit trails cannot determine which code was AI-assisted, breaking compliance traceability.',
    controls: ['CM-3', 'AU-12'],
    frameworks: ['SOC 2', 'ISO 27001'],
  },
  {
    key: 'AI-COMP-001',
    name: 'Regulated Data Path Change',
    category: 'Compliance',
    severity: 'HIGH',
    mode: 'BLOCK',
    enabled: true,
    description: 'Flags changes to code paths handling regulated data (health, financial, PII) without a compliance review annotation. Unreviewed changes to regulated data flows can introduce violations that go undetected until audit.',
    controls: ['PM-11', 'SA-9'],
    frameworks: ['HIPAA', 'PCI DSS', 'SOC 2', 'GDPR'],
  },
  {
    key: 'AI-QA-001',
    name: 'Test Coverage Missing for Security-Sensitive Change',
    category: 'Quality Assurance',
    severity: 'MEDIUM',
    mode: 'WARN',
    enabled: true,
    description: 'Warns when security-sensitive code changes lack corresponding test coverage. Without tests, regressions in authentication, authorization, or input validation would reach production undetected.',
    controls: ['SA-11', 'SI-6'],
    frameworks: ['SOC 2', 'ISO 27001'],
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getSeverityColor = (severity: Severity): 'danger' | 'important' | 'warning' => {
  switch (severity) {
    case 'CRITICAL': return 'danger';
    case 'HIGH': return 'important';
    case 'MEDIUM': return 'warning';
  }
};

const getModeColor = (mode: Mode): 'danger' | 'warning' => {
  switch (mode) {
    case 'BLOCK': return 'danger';
    case 'WARN': return 'warning';
  }
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const CodeGovernancePoliciesPage: React.FC = () => {
  const ps = usePageStyles();
  const styles = useStyles();
  const [policies, setPolicies] = useState<GovernancePolicy[]>(initialPolicies);

  const handleToggle = (key: string) => {
    setPolicies((prev) =>
      prev.map((p) => (p.key === key ? { ...p, enabled: !p.enabled } : p))
    );
  };

  return (
    <div className={ps.page}>
      <PageHeader
        title="AI Code Governance Policies"
        subtitle="Deterministic enforcement rules applied to every pull request"
      />

      {/* Summary */}
      <div className={ps.metricGrid}>
        <Card>
          <div className={ps.cardBody}>
            <div className={ps.statTile}>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Total Policies</Body2>
              <Title2 className={ps.metricValue}>{policies.length}</Title2>
            </div>
          </div>
        </Card>
        <Card>
          <div className={ps.cardBody}>
            <div className={ps.statTile}>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Active</Body2>
              <Title2 className={ps.metricValue} style={{ color: tokens.colorPaletteGreenForeground1 }}>
                {policies.filter((p) => p.enabled).length}
              </Title2>
            </div>
          </div>
        </Card>
        <Card>
          <div className={ps.cardBody}>
            <div className={ps.statTile}>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Block Mode</Body2>
              <Title2 className={ps.metricValue} style={{ color: tokens.colorPaletteRedForeground1 }}>
                {policies.filter((p) => p.mode === 'BLOCK' && p.enabled).length}
              </Title2>
            </div>
          </div>
        </Card>
        <Card>
          <div className={ps.cardBody}>
            <div className={ps.statTile}>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Warn Mode</Body2>
              <Title2 className={ps.metricValue} style={{ color: tokens.colorPaletteYellowForeground1 }}>
                {policies.filter((p) => p.mode === 'WARN' && p.enabled).length}
              </Title2>
            </div>
          </div>
        </Card>
      </div>

      {/* Policy List */}
      <Card>
        <div className={ps.cardBody}>
          <div className={ps.rowList}>
            {policies.map((policy) => (
              <div
                key={policy.key}
                className={styles.policyCard}
                style={{ opacity: policy.enabled ? 1 : 0.6 }}
              >
                {/* Header row */}
                <div className={styles.policyHeader}>
                  <div className={styles.policyHeaderLeft}>
                    <span className={styles.policyKey}>{policy.key}</span>
                    <Body1 style={{ fontWeight: 600 }}>{policy.name}</Body1>
                    <div className={styles.badgeRow}>
                      <Badge appearance="filled" color={getSeverityColor(policy.severity)} size="small">
                        {policy.severity}
                      </Badge>
                      <Badge appearance="filled" color={getModeColor(policy.mode)} size="small">
                        {policy.mode}
                      </Badge>
                      <Badge appearance="outline" size="small">
                        {policy.category}
                      </Badge>
                    </div>
                  </div>
                  <Switch
                    checked={policy.enabled}
                    onChange={() => handleToggle(policy.key)}
                    label={policy.enabled ? 'Enabled' : 'Disabled'}
                  />
                </div>

                {/* Description */}
                <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                  {policy.description}
                </Body2>

                {/* Control & Framework mappings */}
                <div style={{ display: 'flex', gap: tokens.spacingHorizontalXL, flexWrap: 'wrap' }}>
                  <div className={styles.mappingsRow}>
                    <Caption1 className={styles.categoryLabel}>Controls</Caption1>
                    {policy.controls.map((c) => (
                      <Badge key={c} appearance="outline" size="small" color="informative">{c}</Badge>
                    ))}
                  </div>
                  <div className={styles.mappingsRow}>
                    <Caption1 className={styles.categoryLabel}>Frameworks</Caption1>
                    {policy.frameworks.map((fw) => (
                      <Badge key={fw} appearance="tint" size="small" color="brand">{fw}</Badge>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
};
